// Lote de despacho (PO1-2377, épico PO1-2374): rotas /despacho/* do backend_hub
// (instância `api`, que leva o embarcador ativo no header X-Active-Embarcador-Id).
// Erros vêm como { error, code } com 400/404/409.
import api from "./api";

const lista = (data) => (Array.isArray(data) ? data : []);

// [{ id, codigo, idTransportadora, transportadora, status, origem, qtdEnvios,
//    qtdPedidos, datFechamento, datDespacho, numeroColeta, creationTime }]
// params: { status: 'aberto'|'fechado'|'despachado', idTransportadora, de, ate }
export async function listarLotes(params = {}) {
  const { data } = await api.get("/despacho/lotes", { params });
  return lista(data);
}

// Envios com falha do embarcador, em lote ou não:
// [{ idEnvio, idNotaFiscal, numero, pedido, idTransportadora, transportadora,
//    servico, erro, creationTime, idLote, codigoLote }]
export async function listarEnviosComErro() {
  const { data } = await api.get("/despacho/lotes/com-erro");
  return lista(data);
}

// { lote, envios: [{ idEnvio, idNotaFiscal, numero, serie, pedido, destinatario,
//   cidade, uf, codigoRastreio, status, erro, volumes, peso }] }
export async function obterLote(id) {
  const { data } = await api.get(`/despacho/lotes/${id}`);
  return data || { lote: null, envios: [] };
}

// { desvinculados, cancelados, falhasCancelamento: [{ idEnvio, erro }] }
export async function desvincularEnvios(idLote, idsEnvio, cancelarEnvio) {
  const { data } = await api.post(`/despacho/lotes/${idLote}/desvincular`, { idsEnvio, cancelarEnvio: Boolean(cancelarEnvio) });
  return data || { desvinculados: 0, cancelados: 0, falhasCancelamento: [] };
}

// { resultados: [{ idEnvio, ok, status, erro }] } — só envios com status 'falha'.
// Reenviar gera etiqueta nova e cobrança nova na transportadora: o backend exige
// `confirmarCobranca: true` (sem isso, 400 { code: 'confirmacao_cobranca' }); a
// tela confirma com o usuário antes de chamar. Envio já em reenvio volta no
// resultado com erro 'reenvio em andamento'.
export async function reenviarEnviosDoLote(idLote, idsEnvio) {
  const { data } = await api.post(`/despacho/lotes/${idLote}/reenviar`, { idsEnvio, confirmarCobranca: true });
  return data || { resultados: [] };
}

// Mesmo retorno, para envios fora de lote (aba "Com erro").
export async function reenviarEnvios(idsEnvio) {
  const { data } = await api.post("/despacho/envios/reenviar", { idsEnvio, confirmarCobranca: true });
  return data || { resultados: [] };
}

// { lote }
export async function fecharLote(idLote) {
  const { data } = await api.post(`/despacho/lotes/${idLote}/fechar`);
  return data || {};
}

// { lote, coleta: { numeroColeta, erro, falhas: [{ idNotaFiscal, erro }] } | null }
export async function despacharLote(idLote, { solicitarColeta, dataColeta }) {
  const { data } = await api.post(`/despacho/lotes/${idLote}/despachar`, {
    solicitarColeta: Boolean(solicitarColeta),
    dataColeta: solicitarColeta ? dataColeta : undefined,
  });
  return data || {};
}

// Pede (ou refaz) a coleta de um lote já despachado. Mesmo retorno do despachar:
// { lote, coleta: { numeroColeta, erro, falhas[] } }. 409 fora de 'despachado'.
export async function solicitarColetaLote(idLote, dataColeta) {
  const { data } = await api.post(`/despacho/lotes/${idLote}/coleta`, { dataColeta });
  return data || {};
}

// Nome do arquivo do header Content-Disposition (filename="x" ou filename*=UTF-8''x).
export function nomeDoContentDisposition(headers, padrao) {
  const h = headers || {};
  const valor = h["content-disposition"] || h["Content-Disposition"] || "";
  const estendido = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(valor);
  if (estendido && estendido[1]) {
    try {
      return decodeURIComponent(estendido[1].trim().replace(/^"|"$/g, ""));
    } catch (_) {
      // cai no filename simples
    }
  }
  const simples = /filename="?([^";]+)"?/i.exec(valor);
  if (simples && simples[1]) return simples[1].trim();
  return padrao;
}

// Arquivos autenticados: vêm pelo axios como blob e viram <a download> na tela
// (utils/downloadBlob). Devolve { conteudo, nome }.
async function baixar(url, nomePadrao) {
  const resp = await api.get(url, { responseType: "blob" });
  return { conteudo: resp.data, nome: nomeDoContentDisposition(resp.headers, nomePadrao) };
}

// text/csv: NF, pedido, destinatário, cidade/UF, código de rastreio, volumes, peso.
export function baixarManifesto(idLote, codigo) {
  return baixar(`/despacho/lotes/${idLote}/manifesto`, `manifesto-${codigo || idLote}.csv`);
}

// text/plain: NOTFIS 3.1 das notas do lote (gerador da PO1-2378).
export function baixarNotfis(idLote, codigo) {
  return baixar(`/despacho/lotes/${idLote}/notfis`, `NOTFIS-${codigo || idLote}.txt`);
}

// [{ id, idTransportadora, transportadora, tipo: 'horario'|'volume',
//    horario: 'HH:mm'|null, volume|null, ativo }] — TODAS as linhas do
// embarcador, inclusive as inativas (ativo=false).
export async function listarGatilhos() {
  const { data } = await api.get("/despacho/gatilhos");
  return lista(data);
}

// PUT com o ESTADO COMPLETO desejado do embarcador (pode ser []): o que não
// vier no payload é desativado (Ativo=0). Devolve a lista salva, com `ativo`.
export async function salvarGatilhos(gatilhos) {
  const { data } = await api.put("/despacho/gatilhos", { gatilhos: lista(gatilhos) });
  return lista(data);
}

// Transportadoras com vínculo com o embarcador (rota da tela de Pedidos, PO1-2380).
export async function listarTransportadorasPedidos() {
  const { data } = await api.get("/pedidos/transportadoras");
  return lista(data);
}
